'use client';

import { cn } from '@commander/utils';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const switchVariants = cva(
  'peer inline-flex shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none focus-visible:ring-[3px] focus-visible:border-ring focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-input/80',
  {
    variants: {
      size: {
        small: 'h-5 w-9', // 20px height, 36px width
        medium: 'h-[22px] w-10', // 22px height, 40px width
        large: 'h-6 w-11', // 24px height, 44px width
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  }
);

const switchThumbVariants = cva(
  'pointer-events-none block rounded-full bg-background ring-0 transition-transform data-[state=unchecked]:translate-x-0 dark:data-[state=unchecked]:bg-foreground dark:data-[state=checked]:bg-primary-foreground',
  {
    variants: {
      size: {
        small: 'h-4 w-4 data-[state=checked]:translate-x-[18px]', // 16px thumb, 18px translation
        medium: 'h-[18px] w-[18px] data-[state=checked]:translate-x-5', // 18px thumb, 20px translation
        large: 'h-5 w-5 data-[state=checked]:translate-x-[22px]', // 20px thumb, 22px translation
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  }
);

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ className, size, ...props }, ref) => {
    return (
      <SwitchPrimitive.Root
        ref={ref}
        data-slot="switch"
        className={cn(switchVariants({ size }), className)}
        {...props}
      >
        <SwitchPrimitive.Thumb
          data-slot="switch-thumb"
          className={cn(switchThumbVariants({ size }))}
        />
      </SwitchPrimitive.Root>
    );
  }
);
Switch.displayName = SwitchPrimitive.Root.displayName;

export { Switch };
